// dispatch/truckCapacityPolicy.js
// Centralized truck capacity enforcement policy.
// Single source of truth for vehicle dispatch eligibility.
//
// Rules from TRUCK_LEU_CAPACITY.md:
//   Max LEU hard cap = 10
//   10 LEU trucks: Small, Medium, Heavy, Full
//   9 LEU trucks: Small, Medium, Heavy (Full blocked)
//   8 LEU trucks: Small, Medium, Heavy (Full blocked)
//   6 LEU trucks: Small, Medium only (Heavy + Full blocked)
//   Ridgeline: sofa jobs blocked regardless of LEU/tier
//   Maverick: rejected at onboarding — not eligible for dispatch
//
// Original IP — LoadDash Services LLC

import { jobLEU as resolveJobLEU } from "../utils/leuUtils";

// ── Authoritative truck capacity table (from TRUCK_LEU_CAPACITY.md) ────
// Keys match the model strings returned by DriverOnboarding.js detectModel().
// max_leu is the physical floor-footprint capacity of the truck bed.
export const TRUCK_MAX_LEU_TABLE = {
  // 10 LEU — all tiers eligible
  "F-150":           { standard: 10, short: 8, long: 10 },
  "F-250":           { standard: 10, long: 10 },
  "F-350":           { standard: 10, long: 10 },
  "Silverado 1500":  { short: 8, standard: 10, long: 10 },
  "Sierra 1500":     { short: 8, standard: 10, long: 10 },
  "Silverado 2500HD":{ standard: 10, long: 10 },
  "Sierra 2500HD":   { standard: 10, long: 10 },
  "Silverado 3500HD":{ standard: 10, long: 10 },
  "Sierra 3500HD":   { standard: 10, long: 10 },
  "Ram 2500":        { standard: 9, long: 10 },
  "Ram 3500":        { standard: 9, long: 10 },
  "Tundra":          { short: 9, standard: 10, long: 10 },
  // 9 LEU — Full tier blocked
  "Ram 1500":        { short: 8, standard: 9 },
  "Titan":           { short: 8, standard: 9, long: 9 },
  // 8 LEU — Full tier blocked
  "Tacoma":          { short: 6, standard: 8 },
  "Ranger":          { short: 8, standard: 8 },
  "Frontier":        { short: 6, standard: 8 },
  "Ridgeline":       { standard: 8 },
  "Colorado":        { short: 8, standard: 8 },
  "Canyon":          { standard: 8 },
  "Gladiator":       { standard: 8 },
  // 6 LEU — Heavy + Full blocked
  "Santa Cruz":      { standard: 6 },
  // Disqualified — cannot onboard
  "Maverick":        null,
};

// ── Tier minimum VEHICLE LEU thresholds (from reference doc) ───────────
// Minimum vehicle bed capacity in LEU required to be eligible for this tier.
// This is a vehicle-class / safety / headroom gate, NOT the load's job LEU
// value. (A Heavy job is 3 job-LEU but needs a 7+ bed-LEU truck to be allowed;
// a Full job is 6 job-LEU but requires a 10 bed-LEU truck.)
export const TIER_MIN_VEHICLE_LEU = {
  Small:  1,   // Any eligible truck
  Half:   1,   // Any eligible truck (Medium tier)
  ThreeQ: 7,   // Heavy tier — requires 7+ LEU truck
  Full:   10,  // Full tier — requires exactly 10 LEU truck
};

/** @deprecated use TIER_MIN_VEHICLE_LEU */
export const TIER_MIN_LEU = TIER_MIN_VEHICLE_LEU;

// ── Trailer constants ────────────────────────────────────────────────
const TRAILER_LEU_BOOST = 4;
const RIG_MAX_LEU = 10; // Hard cap: truck + trailer cannot exceed 10

// ── Ineligible vehicle models ──────────────────────────────────────────
const INELIGIBLE_MODELS = ["Maverick"];

// ── Special item-type restrictions by model ────────────────────────────
// Ridgeline: 64" bed cannot accept an 84" sofa. Block by item type.
const MODEL_BLOCKED_ITEMS = {
  Ridgeline: ["sofa", "couch", "loveseat"],
};

// ── Normalize vehicle profile ──────────────────────────────────────────
// Converts raw driver_profiles row into a stable shape for policy checks.
export function normalizeVehicleProfile(rawProfile) {
  if (!rawProfile) return null;
  const model = rawProfile.vehicle_model || null;
  const bedSize = rawProfile.bed_size || "standard";
  const modelTable = model ? TRUCK_MAX_LEU_TABLE[model] : undefined;

  // Derive max_leu from reference table if available, else use stored value.
  // Only a model EXPLICITLY listed as null (Maverick) is disqualified. A
  // profile with no vehicle_model recorded must fall through to its stored
  // onboarding max_leu, NOT be zeroed: conflating "model missing" with
  // "model disqualified" blocked a Hauler's completion mid-job on 2026-07-11
  // (VIN + bed_size + max_leu present, vehicle_model null).
  let maxLEU = null;
  if (model && TRUCK_MAX_LEU_TABLE[model] === null) {
    // Model explicitly disqualified (Maverick)
    maxLEU = 0;
  } else if (modelTable) {
    maxLEU = modelTable[bedSize] ?? modelTable.standard ?? null;
  }
  // Fall back to stored max_leu from onboarding if reference table has no entry
  if (maxLEU === null && rawProfile.max_leu != null) {
    maxLEU = Math.min(10, Number(rawProfile.max_leu)); // Hard cap at 10
  }

  const hasTrailer = rawProfile.has_trailer === true;
  const rigLEU = hasTrailer ? Math.min(RIG_MAX_LEU, (maxLEU || 0) + TRAILER_LEU_BOOST) : maxLEU;

  return {
    userId: rawProfile.user_id || null,
    model,
    bedSize,
    maxLEU,
    hasTrailer,
    rigLEU,
    blockedItemTypes: (model && MODEL_BLOCKED_ITEMS[model]) || [],
    raw: rawProfile,
  };
}

// ── Core policy function ───────────────────────────────────────────────
export function getTruckCapacityPolicy(vehicleProfile) {
  const norm = vehicleProfile?.maxLEU != null ? vehicleProfile : normalizeVehicleProfile(vehicleProfile);
  if (!norm) {
    return {
      maxLEU: null,
      allowedTiers: [],
      blockedItemTypes: [],
      vehicleEligible: false,
      ineligibleReason: "missing_vehicle_profile",
    };
  }

  const { model, maxLEU, blockedItemTypes } = norm;

  // Ineligible model check
  if (INELIGIBLE_MODELS.includes(model) || maxLEU === 0) {
    return {
      maxLEU: 0,
      allowedTiers: [],
      blockedItemTypes,
      vehicleEligible: false,
      ineligibleReason: "vehicle_ineligible",
    };
  }

  // Missing capacity data — fail closed
  if (maxLEU == null || maxLEU <= 0) {
    return {
      maxLEU: null,
      allowedTiers: [],
      blockedItemTypes,
      vehicleEligible: false,
      ineligibleReason: "missing_vehicle_profile",
    };
  }

  // Determine allowed tiers based on max_leu
  const allowedTiers = Object.entries(TIER_MIN_VEHICLE_LEU)
    .filter(([, minRequired]) => maxLEU >= minRequired)
    .map(([tierKey]) => tierKey);

  return {
    maxLEU,
    allowedTiers,
    blockedItemTypes,
    vehicleEligible: true,
    ineligibleReason: null,
  };
}

// ── Tier check ─────────────────────────────────────────────────────────
export function isTierAllowedForTruck(vehicleProfile, tierKey) {
  const policy = getTruckCapacityPolicy(vehicleProfile);
  return policy.vehicleEligible && policy.allowedTiers.includes(tierKey);
}

// ── Job-level check ────────────────────────────────────────────────────
// Checks tier, LEU, and item-type restrictions for a specific job.
// Returns backward-compatible shape plus trailer-aware fields.
export function isJobAllowedForTruck(vehicleProfile, job) {
  const norm = vehicleProfile?.maxLEU != null ? vehicleProfile : normalizeVehicleProfile(vehicleProfile);
  const reasons = getBlockedReasonsForTruck(vehicleProfile, job);
  const truckBlocked = reasons.length > 0;

  // Trailer-dependent evaluation: job exceeds truck but fits within rig
  let requiresTrailer = false;
  let exceedsRig = false;

  if (truckBlocked && norm && norm.hasTrailer && norm.rigLEU != null) {
    const jobTier = job.size || job.tier || null;
    const jobLEUVal = resolveJobLEU(job);
    const tierMinLEU = jobTier ? (TIER_MIN_VEHICLE_LEU[jobTier] ?? null) : null;

    // Check if rig (truck + trailer) can handle the job
    const tierFitsRig = tierMinLEU == null || norm.rigLEU >= tierMinLEU;
    const leuFitsRig = jobLEUVal == null || norm.rigLEU >= jobLEUVal;
    // Item-type blocks and vehicle-ineligible are not solvable by trailer
    const hasHardBlock = reasons.includes("blocked_item_type") || reasons.includes("vehicle_ineligible") || reasons.includes("missing_vehicle_profile");

    if (!hasHardBlock && tierFitsRig && leuFitsRig) {
      requiresTrailer = true;
    } else {
      exceedsRig = true;
    }
  } else if (truckBlocked) {
    exceedsRig = true;
  }

  return {
    allowed: !truckBlocked || requiresTrailer,
    requiresTrailer,
    exceedsRig,
    reasons: truckBlocked && !requiresTrailer ? reasons : [],
  };
}

// ── Blocked reasons ────────────────────────────────────────────────────
export function getBlockedReasonsForTruck(vehicleProfile, job) {
  const norm = vehicleProfile?.maxLEU != null ? vehicleProfile : normalizeVehicleProfile(vehicleProfile);
  const reasons = [];

  if (!norm) {
    reasons.push("missing_vehicle_profile");
    return reasons;
  }

  const policy = getTruckCapacityPolicy(norm);

  if (!policy.vehicleEligible) {
    reasons.push("vehicle_ineligible");
    return reasons;
  }

  // Tier check
  const jobTier = job.size || job.tier || null;
  if (jobTier && !policy.allowedTiers.includes(jobTier)) {
    reasons.push("tier_not_allowed");
  }

  // LEU check (canonical waterfall from leuUtils.js)
  const jobLEUVal = resolveJobLEU(job);
  if (jobLEUVal != null && policy.maxLEU != null && jobLEUVal > policy.maxLEU) {
    reasons.push("over_leu_limit");
  }

  // Item-type check (sofa restriction for Ridgeline, etc.)
  if (policy.blockedItemTypes.length > 0 && job.type) {
    const jobItems = job.type.toLowerCase();
    for (const blocked of policy.blockedItemTypes) {
      if (jobItems.includes(blocked)) {
        reasons.push("blocked_item_type");
        break;
      }
    }
  }

  // Conservative Ridgeline fallback: if item type data is absent and
  // vehicle is Ridgeline, block Heavy tier jobs as a conservative fallback.
  // The sofa is the most common Heavy tier item.
  if (norm.model === "Ridgeline" && !job.type && jobTier === "ThreeQ") {
    if (!reasons.includes("blocked_item_type")) {
      reasons.push("blocked_item_type");
    }
  }

  return reasons;
}
